/* istanbul ignore file */
// https://hello.jira.atlassian.cloud/browse/ITFO-20747
import React, { useState } from 'react';
import Help, { ARTICLE_TYPE, RelatedArticles } from '@atlaskit/help';
import { RightSidePanel } from '@atlaskit/right-side-panel';
import { AnalyticsTrackSubject, trackLinkClick } from '@atlassian/temp-analytics';
import { useFeatureToggle } from '@css-xp/base';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { InProductHelpFooter } from './components/in-product-help-footer';
import { getIndexInAlgolia } from './utils/algolia';
import { useGetHomeOptions } from './hooks/use-get-home-options';
import { InProductHelpFeedback } from './components/feedback';
import { DEFAULT_NAVIGATION_DATA, useHelpNavigationHelper } from './hooks/use-help-navigation-helper';
import { useGetHelpArticles } from './hooks/use-get-help-articles';
import { useSearchHelpArticles } from './hooks/use-search-articles';
import { useInProductHelpRouteDetails } from './hooks/use-iph-route-details';

type InProductHelpProps = {
  onClose: () => void;
  openFeedbackWindow: () => void;
};

const InProductHelp: React.FC<InProductHelpProps> = ({ onClose, openFeedbackWindow }) => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);

  const indexInAlgolia = React.useMemo(() => getIndexInAlgolia(isDesignIteration1Enabled), []);

  const { navigationData, setNavigationData, resetHelp } = useHelpNavigationHelper();
  const { getHelpArticle, getRelatedArticles } = useGetHelpArticles(indexInAlgolia);
  const { searchHelpArticles } = useSearchHelpArticles(indexInAlgolia);

  const homeOptions = useGetHomeOptions({ onClickFeedbackOption: openFeedbackWindow });

  const { routeGroup, routeName } = useInProductHelpRouteDetails(isDesignIteration1Enabled);

  return (
    <>
      <Help
        home={{ homeOptions }}
        helpArticle={{
          onGetHelpArticle: getHelpArticle,
        }}
        navigation={{
          navigationData,
          setNavigationData,
        }}
        search={{
          onSearch: searchHelpArticles,
        }}
        relatedArticles={{
          routeGroup: routeGroup,
          routeName: routeName,
          onGetRelatedArticles: getRelatedArticles,
          // this prop is usually used to fire analytics;
          // due to how Help is implemented currently, it is required to make getRelatedArticles work.
          onRelatedArticlesListItemClick: (_event, _analytics, articleData) => {
            trackLinkClick(AnalyticsTrackSubject.SELF_HELP, {
              articleId: articleData?.id,
            });
            return;
          },
        }}
        header={{
          onCloseButtonClick: onClose,
        }}
        footer={<InProductHelpFooter />}
      >
        <RelatedArticles
          onRelatedArticlesListItemClick={(_event, _analytics, articleData) => {
            setNavigationData({
              ...(navigationData ? navigationData : DEFAULT_NAVIGATION_DATA),
              articleId: {
                id: articleData.id,
                type: ARTICLE_TYPE.HELP_ARTICLE,
              },
            });
          }}
          onGetRelatedArticles={getRelatedArticles}
          routeGroup={routeGroup}
          routeName={routeName}
        />
      </Help>
    </>
  );
};

export const InProductHelpWindow: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [isFeedbackWindowOpen, setIsFeedbackWindowOpen] = useState(false);
  const openFeedbackWindow = () => {
    setIsFeedbackWindowOpen(true);
  };
  const onCloseFeedbackWindow = () => {
    setIsFeedbackWindowOpen(false);
  };
  return (
    <>
      <RightSidePanel isOpen={isOpen} attachPanelTo="in-product-help-attach">
        <InProductHelp onClose={onClose} openFeedbackWindow={openFeedbackWindow} />
      </RightSidePanel>
      <InProductHelpFeedback isOpen={isFeedbackWindowOpen} onCloseFeedbackWindow={onCloseFeedbackWindow} />
    </>
  );
};
